/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * Models the NewSDExternalUserSettingsDetailsResponse
 *
 * @class NewSDExternalUserSettingsDetailsResponse
 */
class NewSDExternalUserSettingsDetailsResponse {
  /**
   * Creates an instance of NewSDExternalUserSettingsDetailsResponse.
   * @param {*} json
   * @memberof NewSDExternalUserSettingsDetailsResponse
   */
  constructor(json = null) {
    if (json) {
      // string
      this.cellPhone = json.cellPhone;

      // string
      this.emailAddress = json.emailAddress;

      // string
      this.paperlessInd = json.paperlessInd;

      // string
      this.notificationTypeCode = json.notificationTypeCode;

      // boolean
      this.isEmailVerified = json.isEmailVerified;

      // boolean
      this.isCellPhoneVerified = json.isCellPhoneVerified;
    }
  }
}

/**
 * Models the NewSDExternalUserSettingsDetails
 *
 * @class NewSDExternalUserSettingsDetails
 */
class NewSDExternalUserSettingsDetails {
  /**
   * Creates an instance of NewSDExternalUserSettingsDetails.
   * @param {*} json
   * @memberof NewSDExternalUserSettingsDetails
   */
  constructor(json = null) {
    if (json) {
      // string
      this.cellPhone = json.cellPhone;

      // string
      this.emailAddress = json.emailAddress;

      // string
      this.paperlessInd = json.paperlessInd;

      // string
      this.notificationTypeCode = json.notificationTypeCode;

      // boolean
      this.isEmailVerified = json.isEmailVerified;

      // boolean
      this.isCellPhoneVerified = json.isCellPhoneVerified;
    }
  }
}

/**
 * Models the CodeItem
 *
 * @class CodeItem
 */
class CodeItem {
  /**
   * Creates an instance of CodeItem.
   * @param {*} json
   * @memberof CodeItem
   */
  constructor(json = null) {
    if (json) {
      // string
      this.value = json.value;

      // string
      this.description = json.description;

      // integer
      this.sortOrder = json.sortOrder;

      // ChildCodeTable
      if (json.childCodeTable) {
        this.childCodeTable = new CodeTable(json.childCodeTable);
      }
    }
  }
}

/**
 * Models the CodeTable
 *
 * @class CodeTable
 */
class CodeTable {
  /**
   * Creates an instance of CodeTable.
   * @param {*} json
   * @memberof CodeTable
   */
  constructor(json = null) {
    if (json) {
      // string
      this.tableName = json.tableName;

      // string
      this.defaultValue = json.defaultValue;

      // array - CodeItem
      if (json.codeItems) {
        this.codeItems = json.codeItems.map(object => new CodeItem(object));
      } else if (json.constructor === Array) {
        this.codeItems = json.map(object => new CodeItem(object));
      }
    }
  }
}

/**
 * Models the SDUAIsDuplicate
 *
 * @class SDUAIsDuplicate
 */
class SDUAIsDuplicate {
  /**
   * Creates an instance of SDUAIsDuplicate.
   * @param {*} json
   * @memberof SDUAIsDuplicate
   */
  constructor(json = null) {
    if (json) {
      // boolean
      this.isDuplicate = json.isDuplicate;
    }
  }
}

/**
 * Models the NewSDExternalUserVerificationMode
 *
 * @class NewSDExternalUserVerificationMode
 */
class NewSDExternalUserVerificationMode {
  /**
   * Creates an instance of NewSDExternalUserVerificationMode.
   * @param {*} json
   * @memberof NewSDExternalUserVerificationMode
   */
  constructor(json = null) {
    if (json) {
      // string
      this.verificationMode = json.verificationMode;

      // string
      this.verificationCode = json.verificationCode;
    }
  }
}

/**
 * Models the CodeTableType
 *
 * @class CodeTableType
 */
class CodeTableType {
  /**
   * Creates an instance of CodeTableType.
   * @param {*} json
   * @memberof CodeTableType
   */
  constructor(json = null) {
    if (json) {
      // string
      this.tableName = json.tableName;

      // string
      this.value = json.value;

      // string
      this.description = json.description;

      // string
      this.parentCodeTable = json.parentCodeTable;
    }
  }
}

/**
 * Models the NewSDExternalUserDtlsResponse
 *
 * @class NewSDExternalUserDtlsResponse
 */
class NewSDExternalUserDtlsResponse {
  /**
   * Creates an instance of NewSDExternalUserDtlsResponse.
   * @param {*} json
   * @memberof NewSDExternalUserDtlsResponse
   */
  constructor(json = null) {
    if (json) {
      // string
      this.sdUserName = json.sdUserName;

      // string
      this.emailAddress = json.emailAddress;

      // string
      this.middleName = json.middleName;

      // string
      this.dateOfBirth = json.dateOfBirth;

      // string
      this.ssn = json.ssn;

      // boolean
      this.doesNotHaveAnSSN = json.doesNotHaveAnSSN;

      // string
      this.addressID = json.addressID;

      // string
      this.preferredLanguage = json.preferredLanguage;

      // string
      this.idpCompletedByUser = json.idpCompletedByUser;

      // boolean
      this.idpCompletedByRIDP = json.idpCompletedByRIDP;

      // string
      this.idpCompletedDate = json.idpCompletedDate;

      // object - CodeTableType
      if (json.gender) {
        this.gender = new CodeTableType(json.gender);
      }

      // boolean
      this.idpOptedOut = json.idpOptedOut;

      // boolean
      this.paperlessInd = json.paperlessInd;

      // string
      this.cellPhone = json.cellPhone;

      // boolean
      this.isTempPassword = json.isTempPassword;

      // boolean
      this.linkAcctRequestInd = json.linkAcctRequestInd;

      // boolean
      this.isEmailVerified = json.isEmailVerified;

      // boolean
      this.isCellPhoneVerified = json.isCellPhoneVerified;

      // object - CodeTableType
      if (json.notificationMethod) {
        this.notificationMethod = new CodeTableType(json.notificationMethod);
      }

      // string
      this.verificationCodeTimeStamp = json.verificationCodeTimeStamp;

      // string
      this.verificationCode = json.verificationCode;

      // string
      this.sdRandomAccessToken = json.sdRandomAccessToken;
    }
  }
}

/**
 * Models the NewSDVerificationResultResponse
 *
 * @class NewSDVerificationResultResponse
 */
class NewSDVerificationResultResponse {
  /**
   * Creates an instance of NewSDVerificationResultResponse.
   * @param {*} json
   * @memberof NewSDVerificationResultResponse
   */
  constructor(json = null) {
    if (json) {
      // string
      this.verificationResult = json.verificationResult;
    }
  }
}

export default {
  NewSDExternalUserSettingsDetailsResponse,

  NewSDExternalUserSettingsDetails,

  CodeItem,

  CodeTable,

  SDUAIsDuplicate,

  NewSDExternalUserVerificationMode,

  CodeTableType,

  NewSDExternalUserDtlsResponse,

  NewSDVerificationResultResponse,
};
